// Pure, store-free helpers for the phone appointment card. Nothing here is a
// composable: no Vuex, no reactivity, so specs mount nothing and stub
// nothing.
//
// boxSpanFromEvent is the ONLY place either Mobiscroll date field
// (`startDate` / `endDate`) is read anywhere in this phase, and normalises
// each one exactly once through `valueOf()`. Two things are deliberately
// forbidden on this path:
//
// - Handing either field to a moment constructor. With the timezone plugin
//   this board installs (`mobiscroll/EventCalendar.ts`'s
//   `timezonePlugin: momentTimezone`), `startDate` is the plugin's
//   `createDate` wrapper object rather than a native `Date`. Moment's
//   `isDate` predicate is an `Object.prototype.toString` check that is false
//   for that wrapper, so `moment(startDate)` silently falls through to
//   moment's object parser and returns a valid-looking, today-based instant
//   -- no invalid date, no warning, nothing to notice.
// - Type-guarding either field with `instanceof Date`. The wrapper's declared
//   type extends `Date` structurally, but `Date.prototype` is not in its
//   runtime prototype chain, so the guard is false for the value production
//   actually sends and true for every hand-built spec fixture -- exactly the
//   combination that ships a blank board behind a green suite.
//
// The single normalisation is `valueOf()`, checked with `Number.isFinite`.
import moment from "moment-timezone";

import {
  Appointment,
  AppointmentEvent,
  EventAPIResponse
} from "@/typings/scheduler";

// The middle-dot separator between the compact time range and the street
// address on the AC-01 detail line.
export const CARD_TIME_SEPARATOR = " · ";
// The en dash joining the two compact times, e.g. "9a–11:30a".
export const CARD_RANGE_DASH = "–";
// Matches AppointmentEventSerializer::MIN_VISUAL_DURATION -- the server-side
// 15-minute visual floor this card mirrors client-side.
export const MIN_VISUAL_MINUTES = 15;

// The card's height ladder thresholds, read against the CARD's height: the
// content block is positioned on the card root, so the ladder measures the
// whole appointment rather than the work block alone. Kept in sync by hand
// with the container-query thresholds in MobileAppointmentEvent.vue, since a
// container query cannot read a TypeScript constant.
export const CARD_TITLE_MIN_PX = 30;
export const CARD_DETAIL_MIN_PX = 46;

export type CardVariant = "day" | "week";
export type CardDetail = "color" | "title" | "full";
export type BoxSpan = { topPct: number; heightPct: number };

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Formats an ISO timestamp as a compact time: the hour plus a single
 * meridiem letter on the hour ("9a"), or hour, minutes and a single meridiem
 * letter off the hour ("11:30a"). Defaults to the tenant's own timezone so
 * the function is still callable from a spec with no global tenant set.
 */
export function formatCompactTime(
  iso?: string | null,
  timezone: string | undefined = window.currentTenant?.timezone
): string {
  if (!iso) return "";
  const value = timezone ? moment.tz(iso, timezone) : moment(iso);
  const formatted =
    value.minute() === 0 ? value.format("ha") : value.format("h:mma");
  // "9am" -> "9a", "11:30pm" -> "11:30p"
  return formatted.replace(/m$/, "");
}

/** The two compact times joined by an en dash; empty when either is missing. */
export function formatCompactTimeRange(
  startIso?: string | null,
  endIso?: string | null,
  timezone?: string
): string {
  if (!startIso || !endIso) return "";
  return `${formatCompactTime(startIso, timezone)}${CARD_RANGE_DASH}${formatCompactTime(
    endIso,
    timezone
  )}`;
}

/**
 * Derives the card's box -- a pair of native `Date`s -- from Mobiscroll's
 * render data, clamped to the day the event's cell belongs to. Returns null
 * when `event.date` is not a finite number rather than falling back to the
 * day of `startDate` in the BROWSER's timezone: that fallback would mix two
 * zones (display timezone for `date`, browser-local for the fallback
 * boundary) and mis-scale every segment by the offset between them, for
 * exactly the tenants whose timezone differs from the dispatcher's machine.
 * Losing the multi-day split is the accepted cost; every caller treats a
 * null box as "draw one full-height block", which is today's behaviour.
 *
 * The day end is one calendar day after `dayStart` IN THE DISPLAY TIMEZONE
 * (`window.currentTenant.timezone`, the same zone `EventCalendar.ts` passes
 * Mobiscroll as `displayTimezone`), not a fixed 24h offset: a display day is
 * 23h on that zone's spring-forward date and 25h on its fall-back date, and
 * Mobiscroll clamps its own box to the real next-day boundary regardless.
 * Falling back to the fixed-24h offset when there is no tenant timezone
 * mixes zones the same way the day-start fallback above forbids, so it is
 * the same accepted cost, not a new one.
 */
export function boxSpanFromEvent(
  event: Pick<AppointmentEvent, "startDate" | "endDate" | "date">
): { boxStart: Date; boxEnd: Date } | null {
  const startMs = event.startDate == null ? NaN : event.startDate.valueOf();
  const endMs = event.endDate == null ? NaN : event.endDate.valueOf();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;

  if (!Number.isFinite(event.date)) return null;
  const dayStart = event.date;
  const tz = window.currentTenant?.timezone;
  const dayEnd = tz
    ? moment.tz(dayStart, tz).add(1, "day").valueOf()
    : dayStart + DAY_MS;

  const clampedStart = Math.max(startMs, dayStart);
  const clampedEnd = Math.min(endMs, dayEnd);
  if (clampedEnd <= clampedStart) return null;

  return { boxStart: new Date(clampedStart), boxEnd: new Date(clampedEnd) };
}

/**
 * Proportional top/height percentages of a span inside a box, clamped to the
 * box bounds. The box bounds are already-normalised natives returned by
 * boxSpanFromEvent; a display string handed in here (or any bound whose
 * `valueOf()` is not finite) returns null rather than a nonsense geometry.
 */
export function spanToBox(
  spanStart?: string | null,
  spanEnd?: string | null,
  boxStart?: Date | null,
  boxEnd?: Date | null
): BoxSpan | null {
  if (!spanStart || !spanEnd || !boxStart || !boxEnd) return null;

  const spanStartMs = new Date(spanStart).valueOf();
  const spanEndMs = new Date(spanEnd).valueOf();
  const boxStartMs = boxStart.valueOf();
  const boxEndMs = boxEnd.valueOf();

  if (
    !Number.isFinite(spanStartMs) ||
    !Number.isFinite(spanEndMs) ||
    !Number.isFinite(boxStartMs) ||
    !Number.isFinite(boxEndMs)
  ) {
    return null;
  }

  const boxDuration = boxEndMs - boxStartMs;
  if (boxDuration <= 0) return null;
  if (spanEndMs <= spanStartMs) return null;

  const rawTopPct = ((spanStartMs - boxStartMs) / boxDuration) * 100;
  const rawBottomPct = ((spanEndMs - boxStartMs) / boxDuration) * 100;
  const topPct = Math.min(Math.max(rawTopPct, 0), 100);
  const bottomPct = Math.min(Math.max(rawBottomPct, 0), 100);

  return { topPct, heightPct: Math.max(bottomPct - topPct, 0) };
}

/**
 * Which content the card's ladder shows at a given card height. The week
 * variant is always "title" regardless of height -- a density read, not a
 * short card. This is applied in CSS via a container query; the card itself
 * never calls this at render time because getComponentHtml serialises a
 * static string and cannot measure its own box.
 */
export function cardDetailFor(
  heightPx: number,
  variant: CardVariant
): CardDetail {
  if (variant === "week") return "title";
  if (heightPx >= CARD_DETAIL_MIN_PX) return "full";
  if (heightPx >= CARD_TITLE_MIN_PX) return "title";
  return "color";
}

/**
 * The percentage of the box that MIN_VISUAL_MINUTES occupies, capped at 100.
 * Inert on a single-block card; becomes load-bearing in plan 02 once the
 * work block is one of three segments sharing the box.
 */
export function workFloorPct(boxDurationMinutes: number): number {
  if (!Number.isFinite(boxDurationMinutes) || boxDurationMinutes <= 0) {
    return 100;
  }
  if (boxDurationMinutes <= MIN_VISUAL_MINUTES) return 100;
  return Math.min((MIN_VISUAL_MINUTES / boxDurationMinutes) * 100, 100);
}

// The style guide's three-part chain, drawn inside one Mobiscroll event box.
export type ChainSegmentKind = "pre_work" | "travel" | "late" | "work";

export interface ChainSegment {
  kind: ChainSegmentKind;
  topPct: number;
  heightPct: number;
  first: boolean;
  last: boolean;
  // The work block's top edge BEFORE the MIN_VISUAL_MINUTES floor grew it
  // upward, present on the work segment only. topPct is a display position;
  // this is the temporal edge. Anything comparing the work block against a
  // neighbour's edge must read this -- the floor moves topPct but leaves
  // every lead's bottom where the clock put it, so the two stop meeting.
  rawTopPct?: number;
}

/**
 * Splits an appointment's own box into the style guide's three-part chain --
 * pre-work bar, travel-or-late chevron, work block -- as up to three
 * segments, each pinned to its own start/end inside the `boxStart`/`boxEnd`
 * bound (the clamped `Date` pair `boxSpanFromEvent` returns, never the
 * render data's formatted string fields).
 *
 * The work segment is NEVER conditional and is never derived from the end of
 * the segment above it -- see the two forbidden derivations named in the
 * comments below. Both are wrong on exactly the statuses whose never-null
 * `pre_work_end_time` / `travel_end_time` (`app/models/appointment.rb` lines
 * 415-423) make "the field is present" prove nothing about whether the thing
 * it names has happened.
 */
export function deriveChainSegments(
  appointment: Appointment,
  boxStart: Date,
  boxEnd: Date,
  statusKey: string
): ChainSegment[] {
  const segments: Array<Omit<ChainSegment, "first" | "last">> = [];

  // 1. Pre-work bar. D-03: an accepted_pre_work appointment's whole block
  // already IS that state, so it draws no separate lead segment above it.
  if (statusKey !== "accepted_pre_work") {
    const preWork = spanToBox(
      appointment.pre_work_start_time,
      appointment.pre_work_end_time,
      boxStart,
      boxEnd
    );
    if (preWork) segments.push({ kind: "pre_work", ...preWork });
  }

  // 2. Travel or late -- one segment whose KIND varies, never two conditional
  // segments. The verdict is the record's own `late` flag, NOT
  // getStatusCustomizationName: that helper answers "late" only while the
  // status is still `accepted`, so keying off it would turn the chevron grey
  // the instant the tech arrives and the status moves to `running` -- on the
  // very job the yellow mark exists to flag. V1's TravelEventSerializer uses
  // `object.late?` with no status condition, and D-03 as amended matches it.
  const travelOrLate = spanToBox(
    appointment.travel_start_time,
    appointment.travel_end_time,
    boxStart,
    boxEnd
  );
  if (travelOrLate) {
    segments.push({
      kind: appointment.late ? "late" : "travel",
      ...travelOrLate
    });
  }

  // 3. Work. Reads only the appointment's own fields and the box -- never
  // what steps 1/2 produced.
  //
  // The start rule. Once the tech has ARRIVED (`work_start_time` present),
  // the work block starts there. Before arrival it starts at
  // `arrival_window_start_time` -- the guide's Sec3 anatomy and V1's own
  // `appointment_start_time` (`app/models/appointment.rb` lines 381-396) --
  // with one correction: when NO lead segment was drawn above at all
  // (`accepted_pre_work`, `pending`), the start is pulled up to the box start
  // so the card is one solid block with nothing blank above it. That
  // pull-up is keyed on "no lead segment was drawn", a fact about this
  // derivation's own OUTPUT, not on any timestamp.
  //
  // FORBIDDEN: deriving the start from the end of the segment above it
  // (`travel_end_time` / `pre_work_end_time`). `estimated_end_time` counts
  // from `accepted_time`, stamped at claim and routinely hours old, so
  // `mobiscroll/EventCalendar.ts` closes a travelling box at the CLIENT
  // clock while `travel_end_time` is the SERVER clock
  // (`app/models/appointment.rb` line 417) -- a span between those two is
  // network latency plus or minus skew, sub-second or inverted, and the work
  // block silently vanishes on the commonest travelling card.
  //
  // ALSO FORBIDDEN: work_start_time || travel_end_time || pre_work_end_time.
  // `pre_work_end_time` is `dispatch_time || DateTime.current` and
  // `travel_end_time` is `accepted? ? DateTime.current : arrival_time`, both
  // emitted unconditionally -- so a `pending` appointment reports
  // `pre_work_end_time` as now, and an accepted-not-dispatched one reports
  // both as now. That chain would start the work block at the current
  // clock time on a card where nothing has happened.
  const noLeadSegment = segments.length === 0;
  const workStartIso = appointment.work_start_time
    ? appointment.work_start_time
    : noLeadSegment
      ? boxStart.toISOString()
      : appointment.arrival_window_start_time;
  // The end fallback is load-bearing for its own reason: `work_end_time` is
  // `departure_time`, nil for every RUNNING appointment, and `spanToBox`
  // refuses a half-open span -- without the `boxEnd` fallback every job
  // currently on site would lose its work block entirely.
  const workEndIso = appointment.work_end_time || boxEnd.toISOString();

  let workSpan = spanToBox(workStartIso, workEndIso, boxStart, boxEnd);
  // The work segment is the invariant the rest of the card depends on: it
  // may never be skipped. When its own computed span is degenerate --
  // inverted, zero-length, or wholly outside the box, which the widened
  // phone box (plan 02 Task 2) should prevent but which this pure function
  // can still be handed -- fall back to covering the whole box rather than
  // dropping it, because the title, the detail line and the icon row live
  // inside this segment.
  if (!workSpan) {
    workSpan = { topPct: 0, heightPct: 100 };
  }

  const boxDurationMinutes = (boxEnd.valueOf() - boxStart.valueOf()) / 60000;
  const floorPct = workFloorPct(boxDurationMinutes);
  const rawTopPct = workSpan.topPct;
  if (workSpan.heightPct < floorPct) {
    workSpan = {
      topPct: Math.min(workSpan.topPct, 100 - floorPct),
      heightPct: floorPct
    };
  }

  segments.push({ kind: "work", ...workSpan, rawTopPct });

  // first/last: smallest topPct / largest (topPct + heightPct). Now that the
  // work block is anchored on the arrival window rather than on the segment
  // above it, the chevron above it can start LATER and still end SOONER, so
  // these are not simply the array's first/last entries once a chevron
  // overlaps the work block.
  const minTop = Math.min(...segments.map((s) => s.topPct));
  const topCandidates = segments.filter((s) => s.topPct === minTop);
  // On a tie at the top, the lead segment takes `first` over the work
  // segment, because it is the one that paints there.
  const firstSegment =
    topCandidates.find((s) => s.kind !== "work") ?? topCandidates[0];

  const maxBottom = Math.max(...segments.map((s) => s.topPct + s.heightPct));
  const lastSegment = segments.find(
    (s) => s.topPct + s.heightPct === maxBottom
  )!;

  return segments.map((s) => ({
    ...s,
    first: s === firstSegment,
    last: s === lastSegment
  }));
}

// D-08: the non-appointment/ghost marker kinds a card renders when it is not
// a plain appointment block. "all_day" is the only concept AC-06's
// "all-technician" language maps to on this data model -- no all-technician
// flag exists and none may be added.
export type CardMarkerKind =
  "appointment" | "hold" | "available" | "unavailable" | "all_day";

/**
 * Classifies which of the five marker branches a card renders. `ghost` is
 * computed by the caller (MobileAppointmentEvent.vue already derives it from
 * `is_ghost` / `appointment.ghost`, the same way plan 01 left it) rather than
 * re-derived here, so this stays a pure function of its two inputs.
 *
 * Branch order is significant, not incidental:
 * 1. `ghost` wins over everything else, including an all-day flag on the same
 *    payload -- a hold is a real appointment placeholder and outranks any
 *    calendar-event treatment (the ghost-plus-all-day edge case).
 * 2. A non-ghost payload carrying an appointment is the ordinary case.
 * 3. Everything left is a calendar event: `all_day` first (D-08 -- the only
 *    already-serialized field meaning "spans the day"), then the `exclusive`
 *    split the desktop card already makes (AppointmentEvent.vue lines
 *    91-138).
 */
export function deriveMarkerKind(
  details: EventAPIResponse | undefined,
  ghost: boolean
): CardMarkerKind {
  if (ghost) return "hold";
  if (details?.appointment) return "appointment";
  if (details?.event_details?.all_day) return "all_day";
  return details?.event_details?.exclusive ? "unavailable" : "available";
}
